import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme,
  alpha,
  Tooltip
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  WarningAmber as WarningAmberIcon,
  Info as InfoIcon,
  TrendingUp as TrendingUpIcon,
  Calculate as CalculateIcon,
  CompareArrows as CompareArrowsIcon,
  Home as HomeIcon,
  AccountBalance as AccountBalanceIcon,
  ShowChart as ShowChartIcon
} from '@mui/icons-material';

interface ValuationMethod {
  name: string;
  value: number;
  confidence: 'high' | 'medium' | 'low';
  details: any;
  metadata?: {
    name: string;
    description: string;
    formula: string;
    bestFor: string[];
    limitations: string[];
    parameters: Record<string, any>;
  };
}

interface ValuationMethodsPanelProps {
  methods: Record<string, ValuationMethod>;
  onMethodToggle: (methodKey: string, enabled: boolean) => void;
}

const ValuationMethodsPanel: React.FC<ValuationMethodsPanelProps> = ({
  methods,
  onMethodToggle
}) => {
  const theme = useTheme();
  const [expandedMethod, setExpandedMethod] = useState<string | false>(false);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
      notation: value >= 1000000 ? 'compact' : 'standard'
    }).format(value);
  };

  const getConfidenceColor = (confidence: string) => {
    switch (confidence.toLowerCase()) {
      case 'high': return theme.palette.success.main;
      case 'medium': return theme.palette.warning.main;
      case 'low': return theme.palette.error.main;
      default: return theme.palette.text.secondary;
    }
  };

  const getConfidenceChipColor = (confidence: string): "success" | "warning" | "error" | "default" => {
    switch (confidence.toLowerCase()) {
      case 'high': return "success";
      case 'medium': return "warning";
      case 'low': return "error";
      default: return "default";
    }
  };

  const getMethodIcon = (methodKey: string) => {
    switch (methodKey.toLowerCase()) {
      case 'caprate': return <TrendingUpIcon />;
      case 'revenuemultiple': return <ShowChartIcon />;
      case 'dcf': return <CalculateIcon />;
      case 'salescomparison': return <CompareArrowsIcon />;
      case 'replacementcost': return <HomeIcon />;
      default: return <AccountBalanceIcon />;
    }
  };

  const methodDescriptions: Record<string, { title: string; description: string; whenToUse: string; }> = {
    capRate: {
      title: "Capitalization Rate Method",
      description: "Values the property by dividing Net Operating Income (NOI) by a market-derived capitalization rate.",
      whenToUse: "Best for income-producing properties with stable NOI. Most reliable when comparable sales data is available."
    },
    revenueMultiple: {
      title: "Revenue Multiple Method",
      description: "Values the property by multiplying gross revenue by an industry-standard multiple.",
      whenToUse: "Useful for quick estimates and when detailed expense data is limited. Common in hotel transactions."
    },
    dcf: {
      title: "Discounted Cash Flow Method",
      description: "Projects future cash flows and discounts them to present value using a required rate of return.",
      whenToUse: "Best for properties with growth potential or when detailed projections are available. Most comprehensive approach."
    },
    salesComparison: {
      title: "Sales Comparison Method",
      description: "Values the property based on recent sales of comparable properties in the market.",
      whenToUse: "Most reliable when recent comparable sales are available. Standard approach for appraisals."
    },
    replacementCost: {
      title: "Replacement Cost Method",
      description: "Values the property based on the cost to replace the improvements, less depreciation, plus land value.",
      whenToUse: "Best for new properties or when market data is limited. Common for insurance and special-purpose properties."
    }
  };

  const handleAccordionChange = (methodKey: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedMethod(isExpanded ? methodKey : false);
  };

  const renderMethodDetails = (methodKey: string, method: ValuationMethod) => {
    const details = method.details || {};

    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle2" color="primary" gutterBottom>
          Calculation Details
        </Typography>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          {Object.entries(details).map(([key, value]) => (
            <Grid item xs={6} sm={4} md={3} key={key}>
              <Box sx={{
                p: 1.5,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                backgroundColor: alpha(theme.palette.background.default, 0.5)
              }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {typeof value === 'number'
                    ? (key.toLowerCase().includes('rate') || key.toLowerCase().includes('percentage') || key.toLowerCase().includes('multiple'))
                      ? (value < 1 ? `${(value * 100).toFixed(1)}%` : value.toFixed(2))
                      : value.toLocaleString()
                    : String(value || '-')
                  }
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>

        {method.metadata && (
          <>
            <Typography variant="body2" sx={{ mb: 2, fontStyle: 'italic' }}>
              <strong>Formula:</strong> {method.metadata.formula}
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="success.main" gutterBottom>
                  Best Used For:
                </Typography>
                <List dense>
                  {method.metadata.bestFor.map((item, index) => (
                    <ListItem key={index} sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <CheckCircleOutlineIcon fontSize="small" color="success" />
                      </ListItemIcon>
                      <ListItemText
                        primary={item}
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="warning.main" gutterBottom>
                  Limitations:
                </Typography>
                <List dense>
                  {method.metadata.limitations.map((item, index) => (
                    <ListItem key={index} sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <WarningAmberIcon fontSize="small" color="warning" />
                      </ListItemIcon>
                      <ListItemText
                        primary={item}
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Grid>
            </Grid>
          </>
        )}
      </Box>
    );
  };

  if (!methods || Object.keys(methods).length === 0) {
    return (
      <Box>
        <Typography variant="h5" gutterBottom fontWeight={600}>
          Valuation Methods
        </Typography>
        <Card>
          <CardContent>
            <Typography variant="body1" color="text.secondary">
              No valuation methods available. Please generate a valuation first.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // Separate special methods from standard methods
  const standardMethods = Object.entries(methods).filter(([key, method]) =>
    !method.name.includes("Weighted") && !method.name.includes("AI Enhanced")
  );

  const specialMethods = Object.entries(methods).filter(([key, method]) =>
    method.name.includes("Weighted") || method.name.includes("AI Enhanced")
  );

  return (
    <Box>
      <Typography variant="h5" gutterBottom fontWeight={600}>
        Valuation Methods
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Review and compare different valuation approaches. Each method provides a unique perspective on property value.
      </Typography>

      {/* Special Methods (Weighted Average, AI Enhanced) */}
      {specialMethods.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom color="primary">
            Consolidated Results
          </Typography>
          <Grid container spacing={2}>
            {specialMethods.map(([methodKey, method]) => (
              <Grid item xs={12} sm={6} key={methodKey}>
                <Card
                  elevation={3}
                  sx={{
                    border: `2px solid ${getConfidenceColor(method.confidence)}`,
                    borderRadius: 2
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="h6" fontWeight={600}>
                        {method.name}
                      </Typography>
                      <Chip
                        label={`${method.confidence.toUpperCase()}`}
                        color={getConfidenceChipColor(method.confidence)}
                        size="small"
                        variant="outlined"
                      />
                    </Box>

                    <Typography
                      variant="h4"
                      fontWeight={700}
                      color={getConfidenceColor(method.confidence)}
                      sx={{ mb: 1 }}
                    >
                      {formatCurrency(method.value)}
                    </Typography>

                    <Typography variant="body2" color="text.secondary">
                      {method.name.includes("AI")
                        ? "AI-enhanced valuation incorporating market intelligence"
                        : "Weighted average of all applicable methods"
                      }
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Standard Valuation Methods */}
      <Typography variant="h6" gutterBottom>
        Individual Valuation Methods
      </Typography>

      <Grid container spacing={2}>
        {standardMethods.map(([methodKey, method]) => {
          const methodInfo = methodDescriptions[methodKey] || {
            title: method.name,
            description: "Custom valuation method",
            whenToUse: "Specific use case"
          };

          return (
            <Grid item xs={12} key={methodKey}>
              <Accordion
                expanded={expandedMethod === methodKey}
                onChange={handleAccordionChange(methodKey)}
                sx={{
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: '8px !important',
                  '&:before': { display: 'none' },
                  boxShadow: 'none',
                  '&.Mui-expanded': {
                    margin: '8px 0'
                  }
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    backgroundColor: alpha(theme.palette.background.default, 0.5),
                    borderRadius: '8px',
                    '&.Mui-expanded': {
                      borderBottomLeftRadius: 0,
                      borderBottomRightRadius: 0
                    }
                  }}
                >
                  <Grid container alignItems="center" spacing={2}>
                    <Grid item xs={12} sm={2} md={1}>
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 48,
                        height: 48,
                        borderRadius: '50%',
                        backgroundColor: alpha(getConfidenceColor(method.confidence), 0.1),
                        color: getConfidenceColor(method.confidence)
                      }}>
                        {getMethodIcon(methodKey)}
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={4} md={3}>
                      <Typography variant="subtitle1" fontWeight={600}>
                        {methodInfo.title}
                      </Typography>
                      <Chip
                        label={method.confidence.toUpperCase()}
                        color={getConfidenceChipColor(method.confidence)}
                        size="small"
                        variant="outlined"
                      />
                    </Grid>

                    <Grid item xs={12} sm={3} md={2}>
                      <Typography variant="h6" fontWeight={700} color={getConfidenceColor(method.confidence)}>
                        {formatCurrency(method.value)}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={3} md={6}>
                      <Typography variant="body2" color="text.secondary">
                        {methodInfo.description}
                      </Typography>
                    </Grid>
                  </Grid>
                </AccordionSummary>

                <AccordionDetails sx={{ pt: 2 }}>
                  <Divider sx={{ mb: 2 }} />

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    <strong>When to Use:</strong> {methodInfo.whenToUse}
                  </Typography>

                  {renderMethodDetails(methodKey, method)}
                </AccordionDetails>
              </Accordion>
            </Grid>
          );
        })}
      </Grid>

      {/* Method Comparison Summary */}
      <Card sx={{ mt: 4, backgroundColor: alpha(theme.palette.info.main, 0.05) }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <InfoIcon color="info" sx={{ mr: 1 }} />
            <Typography variant="h6" color="info.main">
              Method Comparison Summary
            </Typography>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" gutterBottom>
                Highest Valuation
              </Typography>
              {(() => {
                const highest = standardMethods.reduce((max, [key, method]) =>
                  method.value > max.value ? method : max, standardMethods[0]?.[1] || { value: 0, name: 'N/A' }
                );
                return (
                  <Typography variant="h6" color="success.main">
                    {highest.name}: {formatCurrency(highest.value)}
                  </Typography>
                );
              })()}
            </Grid>

            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" gutterBottom>
                Lowest Valuation
              </Typography>
              {(() => {
                const lowest = standardMethods.reduce((min, [key, method]) =>
                  method.value < min.value ? method : min, standardMethods[0]?.[1] || { value: 0, name: 'N/A' }
                );
                return (
                  <Typography variant="h6" color="error.main">
                    {lowest.name}: {formatCurrency(lowest.value)}
                  </Typography>
                );
              })()}
            </Grid>

            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" gutterBottom>
                Valuation Range
              </Typography>
              {(() => {
                const values = standardMethods.map(([, method]) => method.value);
                const range = Math.max(...values) - Math.min(...values);
                const variance = ((range / Math.max(...values)) * 100).toFixed(1);
                return (
                  <Typography variant="h6">
                    ±{variance}% ({formatCurrency(range)})
                  </Typography>
                );
              })()}
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ValuationMethodsPanel;